import React, { forwardRef, useImperativeHandle } from "react";
import ApiClient from "../../../services/api-client"
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Tippy from '@tippyjs/react';
import ActionsRenderer from '../aggrid-cell-renderers/actions-renderer'
import SimpleTextRenderer from "../aggrid-cell-renderers/simple-text-renderer";
import IntValEditor from "../aggrid-cell-editors/intval-editor";
import DoubleValRenderer from "../aggrid-cell-renderers/doubleval-renderer";
import useStore from "../../../hooks/useStore";
import { useEditableGrid } from './useEditableGrid';

const EMPTY_ROW = { id: undefined, name: undefined, email: undefined, phone: undefined };

function isValidEmail(email) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
}

const isValid = (data) =>
    data.name != undefined && data.name != "" && isValidEmail(data.email);


const CustomerContactsGrid = forwardRef((props, ref) => {
    const { t } = useTranslation();
    const { hasError, rowStyle, getRowStyle, initGrid, addRow, checkErrors, getItems, setItems, deleteRowLocally } = useEditableGrid({
        isValid,
        emptyRow: EMPTY_ROW,
    });

    const onGridReady = (params) => {
        initGrid(params, props.contacts);
    };

    const onCellValueChanged = (params) => {
        const nodeIndex = params.node.rowIndex;
        const countDisplayedRows = params.api.getDisplayedRowCount() - 1;
        if (nodeIndex == countDisplayedRows && params.newValue != undefined) {
            addRow(params.api);
            params.api.redrawRows(); // re-evaluate getRowStyle for all rows after add
        }
        checkErrors(params.api);
    };

    useImperativeHandle(ref, () => ({
        getData() {
            return getItems();
        },
        hasError() {
            return hasError;
        },
        setData(data) {
            setItems(data);
        }
    }));

    const deleteRow = (params) => {
        const rowData = [];
        const id = params.data.id;
        params.api.forEachNode(node => rowData.push(node.data));
        const companyId = useStore.getState().selectedCompany.id;

        if (id != undefined) {
            ApiClient.deleteCustomerContact(companyId, id).then(
                () => {
                    rowData.splice(params.rowIndex, 1);
                    params.api.setRowData(rowData);
                    toast.success(t("deleteSucceded"));
                },
                error => {
                    toast.error("Error: " + error.response.data.message);
                    console.error(error.response);
                }
            );
        } else {
            deleteRowLocally(params);
        }
    };

    return (
        <div className="container" style={{ padding: 0 }}>
            <div style={{ width: '100%', height: '100%' }}>
                <div className="row">
                    <div className="offset-md-4 col-md-4">
                        <div className="text-center">{t("customerContacts")}
                            <Tippy trigger="click" content={<span className="tooltip-skin">{t("CustomerContactInfo")}</span>}>
                                <i className="fa fa-x2 fa-info-circle infoicon"></i>
                            </Tippy>
                        </div>
                    </div>
                </div>

                <div className="ag-theme-alpine editableSmGrid customerContactsGrid">
                    <AgGridReact
                        onGridReady={onGridReady}
                        rowStyle={rowStyle}
                        getRowStyle={getRowStyle}
                        style={{ width: '100%', height: '300px' }}
                        onCellValueChanged={onCellValueChanged}
                        suppressRowHoverHighlight={true}
                        stopEditingWhenCellsLoseFocus={true}
                        borders={true}
                        singleClickEdit={true}
                        defaultColDef={{
                            flex: 1,
                            resizable: false,
                            sortable: false,
                            wrapText: true,
                            suppressMovable: true,
                        }}
                        columnDefs={[
                            { field: "name", flex: 3, minWidth: 240, headerName: t("Name"), editable: true, cellRenderer: SimpleTextRenderer },
                            { field: "phone", flex: 2, minWidth: 160, headerName: t("Phone"), editable: true, cellEditor: IntValEditor, cellRenderer: DoubleValRenderer, cellRendererParams: { precision: 0 } },
                            { field: "email", flex: 3, minWidth: 200, headerName: t("Email"), editable: true, cellRenderer: SimpleTextRenderer },
                            { field: "action", flex: 1, minWidth: 100, headerName: t("Actions"), cellRenderer: ActionsRenderer, cellRendererParams: { deleteRow: deleteRow } }
                        ]}
                    >
                    </AgGridReact>
                </div>
            </div>
        </div>
    );
});

export default CustomerContactsGrid;
